import { FC, memo, useCallback, useRef } from 'react';
import cn from 'classnames';
import { Table, Spin } from 'antd';
import type {
  SorterResult,
  FilterValue,
  TableCurrentDataSource,
} from 'antd/es/table/interface';
import { ColumnsType } from 'antd/lib/table';
import { IncomingCallsRequest } from '../../../store/reducers/IncomingCallsSlice';
import {
  CurrentIncomingCalls,
  IncomingCallsTablet,
} from '../../../models/IncomingCalls/IncomingCalls';
import { TablePaginationConfig } from 'antd/es/table/interface';
import { useTableHeight } from '../../../hooks/components/useTableHeight';
import styles from './style.module.css';
import tableDefaults from '../../shared/antTableDefaults.module.css';
import { useCallDetailsModal } from '../../../hooks/components/useCallDetailsModal/useCallDetailsModal';
import {
  CallDetailsModal,
  JOURNAL_TASK_STATUS_CONFIG,
} from '../CallDetailsModal/CallDetailsModal';
import { CallAttemptCard } from '../CallDetailsModal/CallAttemptCard/CallAttemptCard';
import { TableFooter } from '../../TableFooter';
import { createDetailsColumn } from '../CallDetailsModal/DetailsColumn';
import { LoadingTable } from '../../shared/Loading';
import { TableResults } from '../../shared/TableResults';
import { callAttemptStateLabel } from '../../../models/CallAttempt/CallAttempt';

interface Props {
  pageSize: number;
  totalCount: number;
  total: number;
  onChange: (page: number, pageItemCount: number) => void;
  callRequest: IncomingCallsRequest;
  allCalls: CurrentIncomingCalls[];
  onTableChange?:
    | ((
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter:
          | SorterResult<IncomingCallsTablet>
          | SorterResult<IncomingCallsTablet>[],
        extra: TableCurrentDataSource<IncomingCallsTablet>,
      ) => void)
    | undefined;
  COLUMNS: ColumnsType<IncomingCallsTablet>;
  divider: { height: number; backgroundColor: string; margin: string };
  handlePageSizeChange: (value: string) => void;
  filtersCollapsed?: boolean;
  scenarioLabelByType?: Record<string, string>;
  resultLabelByValue?: Readonly<Record<string, string>>;
  loading?: boolean;
  hasLoadError?: boolean;
}

export const IncomingCalls: FC<Props> = memo(
  ({
    pageSize,
    onChange,
    callRequest,
    onTableChange,
    total,
    allCalls,
    COLUMNS,
    divider,
    handlePageSizeChange,
    filtersCollapsed,
    scenarioLabelByType = {},
    resultLabelByValue = {},
    loading = false,
    hasLoadError = false,
  }: Props) => {
    const tableContainerRef = useRef<HTMLDivElement | null>(null);

    const { tableHeight, containerHeight, isTabletScroll } = useTableHeight({
      tableContainerRef,
      items: allCalls,
      extraDeps: [filtersCollapsed],
    });

    const tabletScroll = isTabletScroll ? { y: tableHeight } : undefined;

    const {
      isOpen,
      openModal,
      closeModal,
      callAttempts,
      isLoadingAttempts,
      loadingError,
      audioUrls,
      loadAudio,
      transcriptions,
      loadTranscription,
      transcriptionLoading,
      transcriptionAvailability,
      callDetails,
      isLoadingDetails,
      detailsError,
    } = useCallDetailsModal();

    const selectedRecord = useRef<IncomingCallsTablet | null>(null);

    const handleOpenDetails = useCallback(
      (record: IncomingCallsTablet) => {
        selectedRecord.current = record;
        openModal({
          taskId: record.id,
          callType: 'incoming',
          incomingCallId: record.id,
          taskType: record.type,
          taskTypeLabel: record.type
            ? scenarioLabelByType[record.type]
            : undefined,
        });
      },
      [openModal, scenarioLabelByType],
    );

    const columnsWithDetails = [
      ...COLUMNS,
      createDetailsColumn<IncomingCallsTablet>(handleOpenDetails),
    ];

    const rec = selectedRecord.current;

    return (
      <div
        ref={tableContainerRef}
        className={cn(
          styles.IncomingCalls,
          tableDefaults.defaultTableContainer,
        )}
      >
        <TableResults
          isEmpty={!hasLoadError && allCalls.length === 0 && total === 0}
          loading={loading}
        >
          <div style={{ height: `${containerHeight}px` }}>
            <LoadingTable<IncomingCallsTablet>
              columns={columnsWithDetails}
              style={{ tableLayout: 'auto' }}
              className={cn(
                styles.table,
                styles.stickyHeader,
                tableDefaults.defaultTable,
              )}
              rowClassName={styles.customRow}
              onChange={onTableChange}
              pagination={false}
              rowKey={(record) =>
                record.indexId !== undefined ? record.indexId : ''
              }
              showSorterTooltip={false}
              dataSource={allCalls.map((call, index) => ({
                ...call,
                index,
              }))}
              scroll={tabletScroll}
              loading={loading}
            />
          </div>
          <div className={styles.divider} style={divider} />

          <TableFooter
            pageIndex={callRequest.PageIndex}
            pageSize={pageSize}
            total={total}
            onPageChange={onChange}
            onPageSizeChange={handlePageSizeChange}
            disabled={loading}
          />
        </TableResults>

        <CallDetailsModal
          open={isOpen}
          onClose={closeModal}
          taskStatus={rec?.status ?? ''}
          taskResult={rec?.result ?? null}
          resultLabelByValue={resultLabelByValue}
          patientPhone={rec?.phone ?? ''}
          patientFullName={rec?.patientFullname ?? null}
          clinicName={null}
          appointmentDate={null}
          doctorName={null}
          doctorSpecialization={null}
          incomingClinicName={rec?.clinic ?? null}
          incomingStage={rec?.status ?? null}
          incomingCallDate={rec?.beginRaw ?? null}
          callType="incoming"
          details={callDetails}
          isLoadingDetails={isLoadingDetails}
          detailsError={detailsError}
          statusConfigOverride={JOURNAL_TASK_STATUS_CONFIG}
        >
          {isLoadingAttempts ? (
            <div style={{ textAlign: 'center', padding: '24px' }}>
              <Spin />
            </div>
          ) : loadingError ? (
            <div
              style={{
                textAlign: 'center',
                padding: '24px',
                color: '#ff4d4f',
              }}
            >
              {loadingError}
            </div>
          ) : callAttempts.length === 0 ? (
            <div
              style={{ textAlign: 'center', padding: '24px', color: '#8c8c8c' }}
            >
              Для данного задания отсутствуют записи о попытках звонков
            </div>
          ) : (
            [...callAttempts]
              .sort(
                (a, b) =>
                  new Date(a.callDateTime).getTime() -
                  new Date(b.callDateTime).getTime(),
              )
              .map((attempt, index, arr) => {
                const stateLabel = callAttemptStateLabel(
                  attempt.callAttemptState,
                );
                const hasRecording = Boolean(attempt.recording);
                return (
                  <CallAttemptCard
                    key={attempt.id}
                    recordingId={attempt.id}
                    recordingPath={attempt.recording}
                    callDateTime={attempt.callDateTime}
                    callState={stateLabel}
                    hasRecording={hasRecording}
                    audioUrl={audioUrls[attempt.id] ?? null}
                    onLoadAudio={() => loadAudio(attempt.id)}
                    transcription={transcriptions[attempt.id] ?? null}
                    isTranscriptionLoading={
                      transcriptionLoading[attempt.id] ?? false
                    }
                    onLoadTranscription={() => loadTranscription(attempt.id)}
                    hasTranscription={
                      transcriptionAvailability[attempt.id] ?? false
                    }
                    attemptNumber={index + 1}
                    isLast={index === arr.length - 1}
                  />
                );
              })
          )}
        </CallDetailsModal>
      </div>
    );
  },
);

IncomingCalls.displayName = 'IncomingCalls';
